import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, X, Star, ArrowRight } from "lucide-react";

const SPARKLE_OVERLAY = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/4c1845e26a2fcf5b3f9e3f9cca4d1fa8fc996f549630b0ad333e561292a49bc5.png";

const SERVICE_OPTIONS = [
  { id: "residential", label: "Residential" },
  { id: "deep", label: "Deep Cleaning" },
  { id: "office", label: "Office" },
  { id: "airbnb", label: "Airbnb" },
  { id: "movein", label: "Move-In/Out" },
  { id: "carwash", label: "Car Detailing" },
  { id: "mold", label: "Mold Remediation" },
];

export default function AIQuotePage() {
  const [serviceType, setServiceType] = useState("residential");
  const [rooms, setRooms] = useState(2);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(file);
  };

  const generate = async () => {
    if (!image) { toast.error("Please upload a photo of the space"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/quote", {
        service_type: serviceType,
        rooms: Number(rooms) || 1,
        image_base64: image,
      });
      setResult(data);
    } catch (e) {
      toast.error("AI Quote failed", { description: e?.response?.data?.detail || "Try a different photo." });
    } finally { setLoading(false); }
  };

  return (
    <div className="relative" data-testid="ai-quote-page">
      <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none" />
      <div className="relative container-x py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#A88926] text-xs uppercase tracking-[0.24em] mb-6">
            <Sparkles className="w-3.5 h-3.5" /> AI-Powered
          </div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-[#111]">
            Instant Quote, <span className="gold-shimmer-text">Powered by AI.</span>
          </h1>
          <p className="mt-5 text-lg text-[#4A554E]">Snap a photo of the space — Sparkle analyzes the condition, recommends a service, and gives a live price range in seconds.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
          {/* Form */}
          <div className="card-luxe p-7 md:p-8">
            <label className="overline mb-3 block">Service Type</label>
            <div className="flex flex-wrap gap-2 mb-7">
              {SERVICE_OPTIONS.map((s) => (
                <button key={s.id} onClick={() => setServiceType(s.id)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    serviceType === s.id ? "bg-[#0B5D3B] text-white border-[#0B5D3B]" : "border-[#EAE3D2] hover:border-[#0B5D3B]"
                  }`}
                  data-testid={`service-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>

            <label className="overline mb-3 block">Approx. rooms / areas</label>
            <input type="number" min={1} max={20} value={rooms} onChange={(e)=>setRooms(e.target.value)}
              className="w-32 px-5 py-3 rounded-full border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none mb-7"
              data-testid="rooms-input" />

            <label className="overline mb-3 block">Upload a photo</label>
            {image ? (
              <div className="relative rounded-2xl overflow-hidden border border-[#EAE3D2]">
                <img src={image} alt="Preview" className="w-full max-h-72 object-cover" />
                <button onClick={() => setImage(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="remove-image">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-[#EAE3D2] hover:border-[#0B5D3B] rounded-2xl p-10 text-center cursor-pointer transition-colors" data-testid="upload-zone">
                <Upload className="w-8 h-8 text-[#0B5D3B] mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-serif text-xl text-[#111]">Drop a photo here</div>
                <div className="text-xs text-[#7A8A81] mt-1">JPG / PNG · clear, well-lit shots work best</div>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="upload-input" />
              </label>
            )}

            <button onClick={generate} disabled={loading || !image}
              className="btn-primary w-full mt-7 disabled:opacity-60"
              data-testid="generate-quote">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sparkle is analyzing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate AI Quote</>}
            </button>
          </div>

          {/* Result */}
          <div className="card-luxe p-7 md:p-8 sparkle relative overflow-hidden" data-testid="quote-result">
            {!result && !loading && (
              <div className="text-center py-16">
                <Sparkles className="w-12 h-12 text-[#D4AF37]/60 mx-auto mb-4 animate-sparkle" />
                <div className="font-serif text-2xl text-[#111]">Your estimate appears here.</div>
                <div className="text-sm text-[#7A8A81] mt-2">Powered by Claude Sonnet 4.5</div>
              </div>
            )}
            {loading && (
              <div className="text-center py-16">
                <Loader2 className="w-10 h-10 text-[#0B5D3B] mx-auto mb-4 animate-spin" />
                <div className="font-serif text-2xl">Reading every pixel…</div>
              </div>
            )}
            {result && (
              <div>
                <div className="overline mb-3">AI Estimate</div>
                <div className="flex items-baseline gap-2 mb-1">
                  <div className="font-serif text-5xl text-[#0B5D3B]">${Math.round(result.estimated_price_low)}</div>
                  <div className="text-2xl text-[#7A8A81]">– ${Math.round(result.estimated_price_high)}</div>
                </div>
                <div className="text-sm text-[#7A8A81] mb-6">~ {result.estimated_hours} hours · {result.cleaning_type} cleaning</div>
                <p className="text-[#4A554E] mb-6">{result.summary}</p>
                {result.recommendations?.length > 0 && (
                  <>
                    <div className="overline mb-3">Recommendations</div>
                    <ul className="space-y-2 mb-7">
                      {result.recommendations.map((r, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Star className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <Link to="/booking" className="btn-primary w-full" data-testid="book-from-quote">
                  Book this service <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
